/**
 * LocationContext
 *
 * Shared context for all Location Tracking pages. Provides:
 *  - The list of sites for the current tenant (loaded once on mount)
 *  - The list of floor plans for the selected site (loaded when site changes)
 *  - The currently selected siteId + floorId
 *  - setSelection() to change the active site/floor
 *
 * Selection is persisted to URL query params (?siteId=…&floorId=…) so that it
 * survives page navigation within the section and can be deep-linked.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { locationFloorPlansService } from '../services/LocationFloorPlansService';
import { locationUwbService } from '../services/LocationUwbService';
import type {
  FloorPlanConfigWithUrl,
} from '@cnapse/shared/schemas.js';
import { resolveLocationTagPlacement } from '../components/location/locationNavigation';
import { useTenantFeatures } from './TenantFeaturesContext';

export interface SiteOverviewItem {
  siteId: string;
  displayName: string | null;
  tagCounts: { total: number; online: number; offline: number; sos: number; lowBattery: number };
  lastUpdatedAt: string | null;
  siteLat: number | null;
  siteLng: number | null;
  siteAddressLabel: string | null;
  floorCount: number;
  integrationStatus: 'not_configured' | 'configured_inactive' | 'configured_no_recent_data' | 'no_floor_plan' | 'no_registered_tags' | 'active';
  integrationEnabled: boolean;
}

export interface LocationContextValue {
  /** All sites for this tenant. Empty until loaded. */
  sites: SiteOverviewItem[];
  sitesLoading: boolean;
  sitesError: boolean;

  /** Currently selected site ID (from URL params). */
  selectedSiteId: string | null;
  /** Currently selected floor ID (from URL params). */
  selectedFloorId: string | null;

  /** Floor plans for the selected site. Empty if no site is selected or loading. */
  floorPlans: FloorPlanConfigWithUrl[];
  floorPlansLoading: boolean;

  /**
   * Set the active site and optionally a floor plan.
   * Passing null for floorId clears the floor selection.
   * Updates URL query params (?siteId=…&floorId=…).
   */
  setSelection(siteId: string, floorId?: string | null): void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const LocationContext = createContext<LocationContextValue | null>(null);

// ─── URL param helpers ────────────────────────────────────────────────────────

function getUrlParams(): { siteId: string | null; floorId: string | null; tagId: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    siteId: params.get('siteId'),
    floorId: params.get('floorId'),
    tagId: params.get('tagId'),
  };
}

function setUrlParams(siteId: string, floorId?: string | null): void {
  const params = new URLSearchParams(window.location.search);
  params.set('siteId', siteId);
  if (floorId) {
    params.set('floorId', floorId);
  } else {
    params.delete('floorId');
  }
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({}, '', newUrl);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hasSpatialModule, isLoading: featuresLoading } = useTenantFeatures();
  const initial = getUrlParams();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(initial.siteId);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(initial.floorId);
  const [tagDeepLinkPending, setTagDeepLinkPending] = useState(
    () => !initial.siteId && Boolean(initial.tagId),
  );

  // ── Load all sites for this tenant ──────────────────────────────────────────
  const sitesQuery = useQuery({
    queryKey: ['location-sites'],
    queryFn: () => locationUwbService.getLocationSites(),
    staleTime: 5 * 60_000, // 5 minutes
    retry: 2,
    enabled: hasSpatialModule && !featuresLoading,
  });

  const sites = useMemo<SiteOverviewItem[]>(
    () => sitesQuery.data?.data?.sites ?? [],
    [sitesQuery.data],
  );

  // Resolve ?tagId= deep links to the tag's current site/floor before default site selection.
  useEffect(() => {
    const tagId = initial.tagId;
    if (!tagId || initial.siteId) {
      return;
    }

    let cancelled = false;
    void resolveLocationTagPlacement(tagId).then((placement) => {
      if (cancelled) {
        return;
      }
      setTagDeepLinkPending(false);
      if (!placement) {
        return;
      }
      setSelectedSiteId(placement.siteId);
      setSelectedFloorId(placement.floorId);
      setUrlParams(placement.siteId, placement.floorId);
    });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for initial URL
  }, []);

  // Auto-select first site when sites load and nothing is selected yet (skip while refetching to avoid stale picks)
  useEffect(() => {
    if (sitesQuery.isFetching || tagDeepLinkPending) return;
    if (!selectedSiteId && sites.length > 0) {
      const firstSiteId = sites[0].siteId;
      setSelectedSiteId(firstSiteId);
      setUrlParams(firstSiteId, null);
    }
  }, [sites, selectedSiteId, sitesQuery.isFetching, tagDeepLinkPending]);

  // ── Load floor plans for the selected site ───────────────────────────────────
  const floorPlansQuery = useQuery({
    queryKey: ['location-floor-plans', selectedSiteId],
    queryFn: () => locationFloorPlansService.getFloorPlans(selectedSiteId!),
    enabled: !!selectedSiteId,
    staleTime: 5 * 60_000,
  });

  const floorPlans = useMemo<FloorPlanConfigWithUrl[]>(
    () => floorPlansQuery.data?.data?.floorPlans ?? [],
    [floorPlansQuery.data],
  );

  // Auto-select default floor plan (or first) when floor plans load
  useEffect(() => {
    if (tagDeepLinkPending) return;
    if (!selectedFloorId && floorPlans.length > 0) {
      const urlFloorId = getUrlParams().floorId;
      const urlFloor = urlFloorId
        ? floorPlans.find((plan) => plan.floorId === urlFloorId)
        : undefined;
      const def = urlFloor ?? floorPlans.find(f => f.isDefault) ?? floorPlans[0];
      setSelectedFloorId(def.floorId);
      if (selectedSiteId) setUrlParams(selectedSiteId, def.floorId);
    }
  }, [floorPlans, selectedFloorId, selectedSiteId, tagDeepLinkPending]);

  // ── setSelection ─────────────────────────────────────────────────────────────
  const setSelection = useCallback((siteId: string, floorId?: string | null) => {
    const newFloorId = floorId ?? null;
    setSelectedSiteId(siteId);
    setSelectedFloorId(newFloorId);
    setUrlParams(siteId, newFloorId);
  }, []);

  // ── Sync from browser back/forward navigation ─────────────────────────────────
  useEffect(() => {
    const onPopState = () => {
      const p = getUrlParams();
      setSelectedSiteId(p.siteId);
      setSelectedFloorId(p.floorId);
      setTagDeepLinkPending(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const value = useMemo<LocationContextValue>(
    () => ({
      sites,
      sitesLoading: sitesQuery.isLoading,
      sitesError: sitesQuery.isError,
      selectedSiteId,
      selectedFloorId,
      floorPlans,
      floorPlansLoading: floorPlansQuery.isLoading,
      setSelection,
    }),
    [
      sites,
      sitesQuery.isLoading,
      sitesQuery.isError,
      selectedSiteId,
      selectedFloorId,
      floorPlans,
      floorPlansQuery.isLoading,
      setSelection,
    ],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOptionalLocationContext(): LocationContextValue | null {
  return useContext(LocationContext);
}

export function useLocationContext(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return ctx;
}
